import React, { useState, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import CommentPin from './CommentPin';
import { isValidVideo, getMediaTypeFromUrl, getImageUrl } from '../../utils/helpers';

const ImageViewer = ({ imageUrl, mediaType, comments = [], onAddComment, canComment = true, carouselItems = [] }) => {
  const { isAdmin, isDesigner, isWriter } = useAuth();
  const { isRTL } = useLanguage();
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [newCommentPosition, setNewCommentPosition] = useState(null);
  const [newCommentText, setNewCommentText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const imageRef = useRef(null);
  const videoRef = useRef(null);
  
  // Check if we have carousel items
  const hasCarouselItems = carouselItems && carouselItems.length > 0;
  const currentMedia = hasCarouselItems && carouselItems[currentSlide] 
    ? { 
        url: getImageUrl(carouselItems[currentSlide].url), 
        type: carouselItems[currentSlide].type || 'image' 
      }
    : { 
        url: imageUrl, 
        type: mediaType === 'video' || (imageUrl && isValidVideo({ type: imageUrl.match(/\.(mp4|mov|mpeg)$/i) ? 'video/mp4' : '' })) ? 'video' : 'image'
      };
  
  // Determine if current media is video
  const isVideo = currentMedia.type === 'video' || getMediaTypeFromUrl(currentMedia.url) === 'video';
  
  const handlePreviousSlide = () => {
    if (hasCarouselItems) {
      setCurrentSlide((prev) => (prev === 0 ? carouselItems.length - 1 : prev - 1));
    }
  };
  
  const handleNextSlide = () => {
    if (hasCarouselItems) {
      setCurrentSlide((prev) => (prev === carouselItems.length - 1 ? 0 : prev + 1));
    }
  };

  // Only team members can create internal comments
  const canCreateInternal = isAdmin || isDesigner || isWriter;

  const handleMediaClick = (e) => {
    if (!canComment || !isAddingComment) return;
    
    // For videos, disable clicking on controls
    if (e.target.tagName === 'BUTTON' || e.target.closest('video')) {
      return;
    }

    const mediaElement = isVideo ? videoRef.current : imageRef.current;
    if (!mediaElement) return;

    const rect = mediaElement.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setNewCommentPosition({ x, y });
  };

  const handleSaveComment = async () => {
    if (!newCommentText.trim()) return;

    await onAddComment({
      comment_text: newCommentText,
      position_x: newCommentPosition.x,
      position_y: newCommentPosition.y,
      is_internal: isInternal && canCreateInternal,
      // If it's a carousel, include the current slide index
      carousel_item_index: hasCarouselItems ? currentSlide : null,
    });

    // Reset
    setNewCommentText('');
    setNewCommentPosition(null);
    setIsInternal(false);
    setIsAddingComment(false);
  };

  const handleCancelComment = () => {
    setNewCommentText('');
    setNewCommentPosition(null);
    setIsInternal(false);
    setIsAddingComment(false);
  };

  // Filter comments that have position data (pinned to image)
  // For carousel: only show comments for current slide
  // For non-carousel: show all comments with position (carousel_item_index is null)
  const imageComments = comments.filter((comment) => {
    // Must have position data
    if (comment.position_x === null || comment.position_y === null) {
      return false;
    }
    
    // If it's a carousel, only show comments for current slide
    if (hasCarouselItems) {
      // Show comments where carousel_item_index matches currentSlide, or is null (legacy comments)
      return comment.carousel_item_index === currentSlide || comment.carousel_item_index === null;
    } else {
      // For non-carousel posts, only show comments without carousel_item_index
      return comment.carousel_item_index === null || comment.carousel_item_index === undefined;
    }
  });

  return (
    <div className="space-y-4">
      {/* Toggle Add Comment Mode */}
      {canComment && !isAddingComment && !newCommentPosition && (
        <button
          onClick={() => setIsAddingComment(true)}
          className="btn btn-secondary text-sm"
        >
          📍 {isRTL ? 'إضافة تعليق على الصورة' : 'Add Comment on Image'}
        </button>
      )}

      {isAddingComment && !newCommentPosition && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            {isRTL 
              ? `انقر في أي مكان على ${isVideo ? 'الفيديو' : 'الصورة'} لوضع تعليقك`
              : `Click anywhere on the ${isVideo ? 'video' : 'image'} to place your comment`}
          </p>
          <button
            onClick={() => setIsAddingComment(false)}
            className="text-sm text-blue-600 hover:text-blue-700 mt-2"
          >
            {isRTL ? 'إلغاء' : 'Cancel'}
          </button>
        </div>
      )}

      {/* Media Container with Comments */}
      <div className="relative bg-gray-100 rounded-lg overflow-hidden">
        {/* Carousel Navigation (only for carousel with multiple items) */}
        {hasCarouselItems && carouselItems.length > 1 && (
          <>
            <button
              onClick={handlePreviousSlide}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              aria-label="Previous slide"
            >
              <ChevronLeftIcon className="w-6 h-6 text-gray-700" />
            </button>
            <button
              onClick={handleNextSlide}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              aria-label="Next slide"
            >
              <ChevronRightIcon className="w-6 h-6 text-gray-700" />
            </button>
            
            {/* Slide Indicator */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 bg-black/60 px-3 py-1 rounded-full text-white text-xs">
              {currentSlide + 1} / {carouselItems.length}
            </div>
          </>
        )}
        
        {currentMedia.url ? (
          isVideo ? (
            <video
              ref={videoRef}
              src={currentMedia.url}
              controls
              className={`w-full h-auto ${isAddingComment ? 'cursor-crosshair' : ''}`}
              onClick={handleMediaClick}
              key={hasCarouselItems ? currentSlide : 0}
              onError={(e) => {
                console.error('Video failed to load:', currentMedia.url);
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div class="p-8 text-center text-gray-500">Video failed to load</div>';
              }}
            >
              {isRTL ? 'متصفحك لا يدعم تشغيل الفيديو.' : 'Your browser does not support the video tag.'}
            </video>
          ) : (
            <img
              ref={imageRef}
              src={currentMedia.url}
              alt={hasCarouselItems ? `Carousel slide ${currentSlide + 1}` : 'Post content'}
              className={`w-full h-auto ${isAddingComment ? 'cursor-crosshair' : ''}`}
              onClick={handleMediaClick}
              key={hasCarouselItems ? currentSlide : 0}
              onError={(e) => {
                console.error('Image failed to load:', currentMedia.url);
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div class="p-8 text-center text-gray-500">Image failed to load</div>';
              }}
            />
          )
        ) : (
          <div className="w-full aspect-video bg-gray-200 flex items-center justify-center">
            <p className="text-gray-500">{isRTL ? 'لا يوجد محتوى' : 'No content'}</p>
          </div>
        )}

        {/* Existing Comment Pins */}
        {imageComments.map((comment) => (
          <CommentPin
            key={comment.id}
            comment={comment}
            position={{ x: comment.position_x, y: comment.position_y }}
          />
        ))}

        {/* New Comment Pin (being added) */}
        {newCommentPosition && (
          <div
            className="absolute w-8 h-8 -ml-4 -mt-4 bg-yellow-400 border-2 border-white rounded-full shadow-lg flex items-center justify-center text-white font-bold animate-bounce"
            style={{
              left: `${newCommentPosition.x}%`,
              top: `${newCommentPosition.y}%`,
            }}
          >
            ?
          </div>
        )}
      </div>

      {/* New Comment Input (after clicking on image) */}
      {newCommentPosition && (
        <div className="p-4 bg-white border-2 border-yellow-400 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">
            {isRTL ? 'أضف تعليقك' : 'Add your comment'}
          </h4>
          <textarea
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            placeholder={isRTL ? 'ماذا يحتاج للتغيير هنا؟' : 'What needs to be changed here?'}
            className="textarea mb-3"
            rows="3"
            autoFocus
          />
          {canCreateInternal && (
            <label className="flex items-center gap-2 text-sm text-gray-600 mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="flex items-center gap-1">
                <span>🔒</span>
                <span>{isRTL ? 'تعليق داخلي (للفريق فقط)' : 'Internal comment (team only)'}</span>
              </span>
            </label>
          )}
          <div className={`flex ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}>
            <button onClick={handleSaveComment} className="btn btn-primary text-sm">
              {isRTL ? 'حفظ التعليق' : 'Save Comment'}
            </button>
            <button onClick={handleCancelComment} className="btn btn-secondary text-sm">
              {isRTL ? 'إلغاء' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Image Comments Count */}
      {imageComments.length > 0 && (
        <p className="text-sm text-gray-600">
          📍 {imageComments.length} {isRTL 
            ? imageComments.length === 1 ? 'تعليق على الصورة' : 'تعليقات على الصورة'
            : `comment${imageComments.length !== 1 ? 's' : ''} on ${hasCarouselItems ? `slide ${currentSlide + 1}` : 'image'}`}
          {hasCarouselItems && (
            <span className="ml-2 text-gray-400">
              ({isRTL ? 'الصورة الحالية' : 'current slide'})
            </span>
          )}
        </p>
      )}
    </div>
  );
};

export default ImageViewer;


